import { useState } from "react";
import {
  styleSchema,
  styleTypography,
  detachTextStyle,
  snapshotFont,
  defaultTextStyles,
  uid,
  type Project,
  type TypographyOverrides,
  type FontFaceDescriptor,
} from "@proof/core";
import { Select, Button, Control, Toggle } from "./components";
import { TypographyControls, typographyLabel } from "./TypographyControls";
export function TextStyles({
  project: p,
  fonts,
  onChange,
}: {
  project: Project;
  fonts: Map<string, FontFaceDescriptor>;
  onChange: (p: Project) => void;
}) {
  const [selected, setSelected] = useState(p.styles[0]?.id || "");
  const style = p.styles.find((s) => s.id === selected) || p.styles[0];
  const updateDefault = (patch: TypographyOverrides, project = p) =>
    onChange({
      ...project,
      documentSettings: {
        ...project.documentSettings,
        typography: { ...project.documentSettings.typography, ...patch },
      },
    });
  const updateStyle = (patch: TypographyOverrides, project = p) =>
    onChange({
      ...project,
      styles: project.styles.map((s) =>
        s.id === style.id
          ? s.inheritTypography
            ? { ...s, overrides: { ...s.overrides, ...patch } }
            : {
                ...s,
                ...patch,
                overrides: Object.fromEntries(
                  Object.entries(s.overrides).filter(
                    ([key]) =>
                      !Object.prototype.hasOwnProperty.call(patch, key),
                  ),
                ),
              }
          : s,
      ),
    });
  const withFont = (
    face: FontFaceDescriptor | null,
    update: (patch: TypographyOverrides, project?: Project) => void,
  ) => {
    const project = face ? snapshotFont(p, face, [...fonts.values()]) : p;
    update(
      face
        ? {
            fontFaceId: face.id,
            fontFamily: face.family,
            fontWeight: face.weight,
          }
        : { fontFaceId: null, fontFamily: "Inter", fontWeight: 400 },
      project,
    );
  };
  return (
    <>
      <h3>Document defaults</h3>
      <p className="muted">
        Inherited pages and text layers follow these defaults.
      </p>
      <Toggle
        label="Transparent default background"
        value={p.documentSettings.background === null}
        onChange={(transparent) =>
          onChange({
            ...p,
            documentSettings: {
              ...p.documentSettings,
              background: transparent ? null : "#ffffff",
            },
          })
        }
      />
      {p.documentSettings.background !== null && (
        <Control label="Default background color">
          <input
            type="color"
            value={p.documentSettings.background}
            onChange={(e) =>
              onChange({
                ...p,
                documentSettings: {
                  ...p.documentSettings,
                  background: e.target.value,
                },
              })
            }
          />
        </Control>
      )}
      <TypographyControls
        prefix="Default"
        value={p.documentSettings.typography}
        fonts={fonts}
        onChange={updateDefault}
        onFont={(face) => withFont(face, updateDefault)}
      />
      <Control label="Default new layer style">
        <Select
          value={p.documentSettings.defaultStyleId || ""}
          onChange={(e) =>
            onChange({
              ...p,
              documentSettings: {
                ...p.documentSettings,
                defaultStyleId: e.target.value || undefined,
              },
            })
          }
        >
          <option value="">Document typography</option>
          {p.styles.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </Control>
      <div className="control-grid">
        {(["headerStyleId", "footerStyleId"] as const).map((key) => (
          <Control
            key={key}
            label={
              key === "headerStyleId"
                ? "Header text style"
                : "Footer text style"
            }
          >
            <Select
              value={p.settings[key] || ""}
              onChange={(e) =>
                onChange({
                  ...p,
                  settings: {
                    ...p.settings,
                    [key]: e.target.value || undefined,
                  },
                })
              }
            >
              <option value="">Bundled band typography</option>
              {p.styles.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Control>
        ))}
      </div>
      <h4>Reusable text styles</h4>
      <Control label="Editing text style">
        <Select
          value={style?.id || ""}
          onChange={(e) => setSelected(e.target.value)}
        >
          {p.styles.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
              {s.role ? " · Built-in role" : " · Custom"}
            </option>
          ))}
        </Select>
      </Control>
      {style && (
        <>
          <Control label="Style name">
            <input
              value={style.name}
              onChange={(e) =>
                onChange({
                  ...p,
                  styles: p.styles.map((s) =>
                    s.id === style.id ? { ...s, name: e.target.value } : s,
                  ),
                })
              }
            />
          </Control>
          <Toggle
            label="Inherit document typography"
            value={style.inheritTypography}
            onChange={(inherit) =>
              onChange({
                ...p,
                styles: p.styles.map((s) =>
                  s.id === style.id
                    ? inherit
                      ? { ...s, inheritTypography: true, overrides: {} }
                      : {
                          ...s,
                          ...styleTypography(p, s),
                          inheritTypography: false,
                          overrides: {},
                        }
                    : s,
                ),
              })
            }
          />
          <TypographyControls
            prefix="Style"
            value={styleTypography(p, style)}
            fonts={fonts}
            onChange={updateStyle}
            onFont={(face) => withFont(face, updateStyle)}
          />
          {style.inheritTypography && (
            <>
              <p className="muted">
                {Object.keys(style.overrides).length} configured properties;
                other values inherit from the document.
              </p>
              <div className="button-row">
                {Object.keys(style.overrides).map((key) => (
                  <Button
                    key={key}
                    onClick={() => {
                      const overrides = { ...style.overrides };
                      delete overrides[key as keyof TypographyOverrides];
                      onChange({
                        ...p,
                        styles: p.styles.map((s) =>
                          s.id === style.id ? { ...s, overrides } : s,
                        ),
                      });
                    }}
                  >
                    Reset {typographyLabel(key)}
                  </Button>
                ))}
              </div>
              <Button
                onClick={() =>
                  onChange({
                    ...p,
                    styles: p.styles.map((s) =>
                      s.id === style.id
                        ? {
                            ...s,
                            overrides:
                              defaultTextStyles().find(
                                (b) => b.role === style.role,
                              )?.overrides || {},
                          }
                        : s,
                    ),
                  })
                }
              >
                Reset style overrides
              </Button>
            </>
          )}
          <Button
            disabled={
              !!style.role ||
              p.settings.headerStyleId === style.id ||
              p.settings.footerStyleId === style.id
            }
            onClick={() =>
              onChange({
                ...p,
                styles: p.styles.filter((s) => s.id !== style.id),
                documentSettings: {
                  ...p.documentSettings,
                  defaultStyleId:
                    p.documentSettings.defaultStyleId === style.id
                      ? undefined
                      : p.documentSettings.defaultStyleId,
                },
                pages: p.pages.map((pg) => ({
                  ...pg,
                  nodes: pg.nodes.map((n) =>
                    n.styleId === style.id ? detachTextStyle(p, n) : n,
                  ),
                })),
              })
            }
          >
            Remove text style
          </Button>
        </>
      )}
      <Button
        onClick={() => {
          const s = styleSchema.parse({
            id: uid("style"),
            name: "New style",
            inheritTypography: true,
          });
          onChange({ ...p, styles: [...p.styles, s] });
          setSelected(s.id);
        }}
      >
        Add style
      </Button>
    </>
  );
}
